import { useMemo, useState } from "react";
import {
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Heart,
  Flame,
  MessageCircle,
  Camera,
  User,
  Brain,
  Compass,
  Shield,
  TrendingUp,
  ThumbsDown,
  Lightbulb,
  Crown,
  Copy,
  Check,
} from "lucide-react";
import { SwipeDeck } from "./SwipeDeck";
import {
  RadarScoresChart,
  BigFiveSpider,
  OverallRadial,
  ConversationHeatmap,
  PhotoCarousel,
} from "./ReportCharts";
import { useLang } from "../i18n/LangContext";
import { toast } from "sonner";

const MODE_ICON = {
  conversation: MessageCircle,
  profile: User,
  photos: Camera,
  full: Sparkles,
};

/* ------------------------ Card base layout ------------------------ */
function CardShell({
  hue = "#ff4458",
  hue2 = "#fd267a",
  badge,
  badgeIcon: BadgeIcon,
  scoreLabel,
  scoreValue,
  title,
  subtitle,
  children,
  bgImage,
  age,
}) {
  return (
    <div className="relative flex h-full w-full flex-col">
      {/* Top visual area – Tinder-style large photo / gradient */}
      <div
        className="relative h-[58%] w-full overflow-hidden"
        style={{
          background: bgImage
            ? `url(${bgImage}) center/cover no-repeat`
            : `radial-gradient(circle at 20% 20%, ${hue}cc 0%, ${hue2}99 45%, #0f1117 100%)`,
        }}
      >
        {/* Soft pattern */}
        {!bgImage && (
          <div
            className="absolute inset-0 opacity-30 mix-blend-overlay"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)",
              backgroundSize: "22px 22px",
            }}
          />
        )}
        {/* Bottom fade to card surface */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#181b24] via-[#181b24]/80 to-transparent" />

        {/* Top badge */}
        {badge && (
          <div className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-black/30 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white backdrop-blur-md">
            {BadgeIcon ? <BadgeIcon className="h-3 w-3" /> : null}
            {badge}
          </div>
        )}

        {/* Score chip */}
        {scoreValue !== undefined && scoreValue !== null && (
          <div className="absolute right-4 top-4 flex items-center gap-2 rounded-2xl border border-white/25 bg-black/40 px-3 py-1.5 backdrop-blur-md">
            <div className="text-right">
              <div className="font-display text-xl font-extrabold leading-none text-white">
                {scoreValue}
              </div>
              <div className="text-[8px] font-bold uppercase tracking-wider text-white/70">
                {scoreLabel || "score"}
              </div>
            </div>
          </div>
        )}

        {/* Title at bottom of photo area (Tinder name + age) */}
        <div className="absolute inset-x-5 bottom-4">
          <div className="flex items-end gap-2">
            <h2 className="font-display text-3xl font-extrabold leading-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
              {title}
            </h2>
            {age !== undefined && age !== null && (
              <span className="pb-1 font-display text-2xl font-light text-white/95 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                {age}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="mt-1 line-clamp-1 text-sm font-medium text-white/85 drop-shadow">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Lower content area */}
      <div className="flex-1 overflow-y-auto bg-[#181b24] px-5 py-4">
        {children}
      </div>
    </div>
  );
}

/* ------------------------ Card variants ------------------------ */
function BulletList({ items, color = "#ff4458", emptyText = "—" }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-[#a8adbd]">{emptyText}</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-[13.5px] leading-relaxed text-white">
          <span
            className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full"
            style={{ background: color }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function MessageCard({ value, pool }) {
  const variants = (pool && pool.length > 0) ? pool : (value ? [value] : []);
  const [idx, setIdx] = useState(0);
  const [copied, setCopied] = useState(false);

  const current = variants[idx] || value;
  if (!current) return <p className="text-sm text-[#a8adbd]">—</p>;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(current);
      setCopied(true);
      toast.success("Skopiowano!");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const cycle = (dir) => {
    if (variants.length <= 1) return;
    setIdx((i) => (i + dir + variants.length) % variants.length);
  };

  return (
    <div className="space-y-2">
      <div className="relative rounded-2xl border border-[#222636] bg-[#0f1117] p-4 pr-12">
        <p className="text-[14px] leading-relaxed text-white">{current}</p>
        <button
          type="button"
          onClick={copy}
          className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-[#222636] text-[#a8adbd] transition-colors hover:bg-[#ff4458]/20 hover:text-white"
        >
          {copied ? <Check className="h-4 w-4 text-[#32d583]" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>

      {variants.length > 1 && (
        <div className="flex items-center justify-between gap-2 px-1">
          <button
            type="button"
            onClick={() => cycle(-1)}
            className="inline-flex items-center gap-1 rounded-full border border-[#222636] bg-[#181b24] px-3 py-1 text-[11px] font-semibold text-[#a8adbd] transition hover:border-[#ff4458]/40 hover:text-white"
          >
            ‹ Poprz.
          </button>
          <div className="flex items-center gap-1">
            {variants.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-5 bg-[#ff4458]" : "w-1.5 bg-white/20"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => cycle(1)}
            className="inline-flex items-center gap-1 rounded-full border border-[#222636] bg-[#181b24] px-3 py-1 text-[11px] font-semibold text-[#a8adbd] transition hover:border-[#ff4458]/40 hover:text-white"
          >
            Nast. ›
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------ Build deck from report ------------------------ */
export function ReportDeckView({ doc, tier = "standard" }) {
  const { t } = useLang();
  const r = doc.report || {};
  const scores = r.scores || {};
  const ModeIcon = MODE_ICON[doc.mode] || Sparkles;
  const isPremium = tier === "premium";

  const cards = useMemo(() => {
    const list = [];

    /* 1. Overall - hero card */
    list.push({
      id: "overall",
      render: () => (
        <CardShell
          hue="#ff4458"
          hue2="#fd267a"
          badge={isPremium ? "PREMIUM" : "STANDARD"}
          badgeIcon={isPremium ? Crown : Flame}
          scoreLabel="Overall"
          scoreValue={doc.overall_score}
          title={r.title || (isPremium ? "Premium Report" : "Twoja analiza")}
          subtitle={doc.contact_name || (doc.mode ? `tryb: ${doc.mode}` : null)}
        >
          <div className="space-y-3">
            <p className="text-[13.5px] leading-relaxed text-[#dcdde6]">
              {r.summary || "—"}
            </p>
            {(doc.tags || r.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(doc.tags || r.tags || []).map((tg, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-[#222636] px-2.5 py-1 text-[10.5px] font-medium text-[#a8adbd]"
                  >
                    {tg}
                  </span>
                ))}
              </div>
            )}
          </div>
        </CardShell>
      ),
    });

    /* 2. Scores - now with Radar chart */
    const scoreDefs = [
      { key: "response_chance", invert: false },
      { key: "interest", invert: false },
      { key: "meeting_chance", invert: false },
      { key: "chemistry", invert: false },
      { key: "ghosting_risk", invert: true },
      { key: "profile_strength", invert: false },
      { key: "photo_quality", invert: false },
      { key: "bio_quality", invert: false },
    ];
    const hasScores = Object.keys(scores).length > 0;
    if (hasScores) {
      const labels = {};
      scoreDefs.forEach((s) => {
        labels[s.key] = t(`score_${s.key}`) || s.key;
      });
      list.push({
        id: "scores-radar",
        render: () => (
          <CardShell
            hue="#fdb022"
            hue2="#ff6036"
            badge="Metryki AI"
            badgeIcon={TrendingUp}
            scoreLabel="Match"
            scoreValue={doc.overall_score}
            title={t("scoresTitle") || "Radar wyników"}
            subtitle="8 osi · animowany"
          >
            <RadarScoresChart scores={scores} labels={labels} color="#ff4458" />
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[10.5px]">
              {scoreDefs.slice(0, 6).map((s) => (
                <div
                  key={s.key}
                  className="flex items-center justify-between rounded-md bg-[#0f1117] px-2 py-1"
                >
                  <span className="text-[#a8adbd]">{labels[s.key]}</span>
                  <span className="font-mono font-semibold text-white">
                    {scores[s.key] ?? 0}
                  </span>
                </div>
              ))}
            </div>
          </CardShell>
        ),
      });

      /* 2b. Overall radial gauge */
      list.push({
        id: "overall-radial",
        render: () => (
          <CardShell
            hue="#ff4458"
            hue2="#fd267a"
            badge="Overall Score"
            badgeIcon={Sparkles}
            scoreLabel={null}
            scoreValue={null}
            title="Wynik ogólny"
            subtitle="Zważony średni · 0-100"
          >
            <OverallRadial value={doc.overall_score ?? 0} label="Overall" />
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
              {scoreDefs.slice(0, 4).map((s) => (
                <div
                  key={s.key}
                  className="rounded-lg bg-[#0f1117] px-3 py-2"
                >
                  <div className="text-[9px] uppercase tracking-wider text-[#a8adbd]">
                    {labels[s.key]}
                  </div>
                  <div className="mt-0.5 font-display text-base font-bold text-white">
                    {scores[s.key] ?? 0}
                  </div>
                </div>
              ))}
            </div>
          </CardShell>
        ),
      });
    }

    /* 2c. Conversation heatmap (only if text/scores present) */
    const conversationText = doc.conversation_text || "";
    if (conversationText || hasScores) {
      list.push({
        id: "heatmap",
        render: () => (
          <CardShell
            hue="#ff6036"
            hue2="#fd267a"
            badge="Heatmap"
            badgeIcon={Flame}
            scoreLabel="Intensywność"
            scoreValue={Math.min(99, Math.max(0, Math.round((doc.overall_score || 0))))}
            title="Energia rozmowy"
            subtitle="Od początku do końca"
          >
            <ConversationHeatmap text={conversationText} scores={scores} />
            <p className="mt-3 text-[11.5px] leading-relaxed text-[#a8adbd]">
              Wyższe słupki = momenty z największą intensywnością wymiany
              (pytania zwrotne, emocje, konkrety).
            </p>
          </CardShell>
        ),
      });
    }

    /* 2d. Photo carousel (only if images stored) */
    const thumbs = doc.image_thumbnails || [];
    if (thumbs.length > 0) {
      list.push({
        id: "photos",
        render: () => (
          <CardShell
            hue="#a855f7"
            hue2="#ec4899"
            badge="Załączniki"
            badgeIcon={Camera}
            scoreLabel="Zdjęć"
            scoreValue={thumbs.length}
            title="Materiał z analizy"
            subtitle="Wczytane screeny / zdjęcia"
          >
            <PhotoCarousel images={thumbs} />
            <p className="mt-3 text-[11.5px] leading-relaxed text-[#a8adbd]">
              AI Vision przeanalizowało powyższe zdjęcia podczas tworzenia raportu.
            </p>
          </CardShell>
        ),
      });
    }

    /* 3. Positive signals */
    if ((r.positive_signals || []).length) {
      list.push({
        id: "positive",
        render: () => (
          <CardShell
            hue="#32d583"
            hue2="#10b981"
            badge="Plusy"
            badgeIcon={CheckCircle2}
            scoreLabel="Signals"
            scoreValue={(r.positive_signals || []).length}
            title={t("positiveSignals") || "Pozytywne sygnały"}
            subtitle="To działa na Twoją korzyść"
          >
            <BulletList items={r.positive_signals} color="#32d583" />
          </CardShell>
        ),
      });
    }

    /* 4. Negative signals */
    if ((r.negative_signals || []).length) {
      list.push({
        id: "negative",
        render: () => (
          <CardShell
            hue="#fd267a"
            hue2="#ff4458"
            badge="Uwaga"
            badgeIcon={AlertTriangle}
            scoreLabel="Risks"
            scoreValue={(r.negative_signals || []).length}
            title={t("negativeSignals") || "Negatywne sygnały"}
            subtitle="Te elementy szkodzą"
          >
            <BulletList items={r.negative_signals} color="#fd267a" />
          </CardShell>
        ),
      });
    }

    /* 5. Diagnosis */
    if (r.diagnosis) {
      list.push({
        id: "diagnosis",
        render: () => (
          <CardShell
            hue="#a855f7"
            hue2="#6366f1"
            badge="Diagnoza"
            badgeIcon={Brain}
            title={t("diagnosis") || "Diagnoza"}
            subtitle="Co tu się dzieje"
          >
            <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-[#dcdde6]">
              {r.diagnosis}
            </p>
          </CardShell>
        ),
      });
    }

    /* 6. Next step */
    if (r.next_step) {
      list.push({
        id: "nextstep",
        render: () => (
          <CardShell
            hue="#ff4458"
            hue2="#ff6036"
            badge="Następny krok"
            badgeIcon={Heart}
            title={t("nextStep") || "Następny krok"}
            subtitle="Zrób to teraz"
          >
            <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-white">
              {r.next_step}
            </p>
          </CardShell>
        ),
      });
    }

    /* 7. Message suggestions - one card per tone */
    const msgs = r.message_suggestions || {};
    const pools = r.message_pools || {};
    const msgKeys = ["safe", "casual", "flirty", "specific"];
    const tones = {
      safe: { hue: "#3b82f6", hue2: "#0ea5e9", emoji: "🛡" },
      casual: { hue: "#10b981", hue2: "#32d583", emoji: "💬" },
      flirty: { hue: "#fd267a", hue2: "#ff4458", emoji: "🔥" },
      specific: { hue: "#fdb022", hue2: "#ff6036", emoji: "🎯" },
    };
    msgKeys.forEach((k) => {
      if (msgs[k] || (pools[k] && pools[k].length > 0)) {
        list.push({
          id: `msg-${k}`,
          render: () => (
            <CardShell
              hue={tones[k].hue}
              hue2={tones[k].hue2}
              badge={`Wiadomość: ${t(`msg_${k}`) || k}`}
              badgeIcon={MessageCircle}
              title={t(`msg_${k}`) || k}
              subtitle={pools[k] && pools[k].length > 1 ? `${pools[k].length} wariantów` : "Skopiuj i wyślij"}
            >
              <MessageCard value={msgs[k]} pool={pools[k]} />
            </CardShell>
          ),
        });
      }
    });

    /* 8. Donts */
    if ((r.donts || []).length) {
      list.push({
        id: "donts",
        render: () => (
          <CardShell
            hue="#ef4444"
            hue2="#fd267a"
            badge="Czerwone flagi"
            badgeIcon={ThumbsDown}
            title={t("donts") || "Czego nie robić"}
            subtitle="Te błędy kosztują match"
          >
            <BulletList items={r.donts} color="#ef4444" />
          </CardShell>
        ),
      });
    }

    /* 9. Profile tips */
    if ((r.profile_tips || []).length) {
      list.push({
        id: "tips",
        render: () => (
          <CardShell
            hue="#fdb022"
            hue2="#ff6036"
            badge="Tips"
            badgeIcon={Lightbulb}
            title={t("profileTips") || "Sugestie poprawy"}
            subtitle="Łatwe wygrane"
          >
            <BulletList items={r.profile_tips} color="#fdb022" />
          </CardShell>
        ),
      });
    }

    /* PREMIUM extras */
    if (isPremium) {
      const p = r.personality || {};
      if (Object.keys(p).length) {
        list.push({
          id: "personality",
          render: () => (
            <CardShell
              hue="#a855f7"
              hue2="#ec4899"
              badge="Big Five · Personality"
              badgeIcon={Brain}
              scoreLabel="Profil"
              scoreValue={Math.round(
                (["openness","conscientiousness","extraversion","agreeableness"].reduce(
                  (acc, k) => acc + (p[k] ?? 0),
                  0
                ) + (100 - (p.neuroticism ?? 50))) / 5
              )}
              title={t("personality") || "Osobowość"}
              subtitle="5-osiowy spider chart"
            >
              <BigFiveSpider personality={p} color="#a855f7" />
              {p.summary && (
                <p className="mt-2 text-[12.5px] leading-relaxed text-[#dcdde6]">
                  {p.summary}
                </p>
              )}
            </CardShell>
          ),
        });
      }

      const a = r.attachment_style || {};
      if (a.style || a.summary) {
        list.push({
          id: "attachment",
          render: () => (
            <CardShell
              hue="#0ea5e9"
              hue2="#6366f1"
              badge={a.style || "Attachment"}
              badgeIcon={Shield}
              title={t("attachmentStyle") || "Styl przywiązania"}
              subtitle={a.style ? `Typ: ${a.style}` : undefined}
            >
              <p className="text-[13.5px] leading-relaxed text-[#dcdde6]">{a.summary || "—"}</p>
            </CardShell>
          ),
        });
      }

      const s = r.approach_strategy || {};
      if (s.summary || (s.steps && s.steps.length)) {
        list.push({
          id: "strategy",
          render: () => (
            <CardShell
              hue="#fdb022"
              hue2="#ff4458"
              badge="Strategy"
              badgeIcon={Compass}
              title={t("approachStrategy") || "Strategia podejścia"}
              subtitle="Plan działania"
            >
              {s.summary && (
                <p className="mb-3 text-[13.5px] leading-relaxed text-[#dcdde6]">{s.summary}</p>
              )}
              {s.steps && s.steps.length > 0 && (
                <ol className="space-y-2">
                  {s.steps.map((step, i) => (
                    <li key={i} className="flex gap-2.5 text-[13px] leading-relaxed text-white">
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full dc-gradient-bg text-[10px] font-bold text-white">
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              )}
            </CardShell>
          ),
        });
      }
    }

    return list;
  }, [doc, r, scores, t, isPremium]);

  return <SwipeDeck cards={cards} />;
}
